import { AddRefusalCard } from 'components';
import { SecondaryPopup } from 'components/pop-up';
import { TripContext } from 'contexts';
import { SecondaryPopUpField } from 'enums';
import React, { Fragment, useContext, useState } from 'react';
import { useOrderItemService, useParamService, useReturnItemService } from 'services';
import { ReasonClass, ReconcileReasonType } from 'services/enums';
import { OrderItem, OrderItemChange, ReconcileReason, RefusalReason } from 'services/types';
import { SecondaryPopUpValue } from 'types';
import './add-refusal.scss';

export const AddRefusal = () => {

    const { getIntValue } = useParamService
    const { updateOrderItemChange, resetOrderItemChangeApproval } = useOrderItemService()
    const { isReturnStickerDuplicate } = useReturnItemService()

    const [isSecondaryPopupVisible, setSecondaryPopupVisible] = useState<boolean>(false)
    const [selectedOrderItemChanges, setSelectedOrderItemChanges] = useState<SecondaryPopUpValue>({} as SecondaryPopUpValue)
    const [isReturnStickerAvailable, setIsReturnStickerAvailable] = useState<boolean>(true)

    const orderItemId: number | undefined = getIntValue('orderItemId')
    const tripContext = useContext(TripContext)
    const refusalReasons: RefusalReason[] = tripContext.refusalReasons.toArray() ?? []
    const reconcileReasons: ReconcileReason[] = tripContext.reconcileReasons.filter(reason => reason.isRefusal && reason.class === ReasonClass.Refused)
    const orderItem: OrderItem = tripContext.orderItems.find(oi => oi.id === orderItemId) ?? {} as OrderItem
    const orderItemChanges: OrderItemChange[] = tripContext.orderItemChanges.filter(change => change.orderItemId === orderItem.id)

    let orderItemChangeQuantity: number = 0
    orderItemChanges.forEach(change => {
        if (change.reconcileReasonId != ReconcileReasonType.OverageKept)
            orderItemChangeQuantity += change.quantity
    })

    let shippedQuantity: number = orderItem.shippedQuantity > orderItem.countedQuantity ? orderItem.shippedQuantity : orderItem.countedQuantity
    const deliveredQuantity: number = shippedQuantity - orderItemChangeQuantity


    const handleItemQuantityChanged = (reconcileId: number, quantity: number): void => {
        let change: OrderItemChange | undefined =  tripContext.orderItemChanges.filter(change => change.orderItemId === orderItem.id)
                                                                               .find(change => change.reconcileReasonId === reconcileId)

        const item: OrderItem | undefined = tripContext.orderItems.find(oi => oi.id == change?.orderItemId)

        if (change != null) {
            change.quantity = quantity
            updateOrderItemChange(change)
            item != undefined && resetOrderItemChangeApproval(item)
        }
        else {
            const newChange: OrderItemChange = {
                id: new Date().getTime(),
                orderItemId: orderItem.id,
                quantity: quantity,
                reconcileReasonId: reconcileId,
                noReturnStickerReason: null,
                refusalChoiceId: null,
                refusalComment: null,
                returnSticker: null,
                codeDate: null,
                reconcileReason: reconcileReasons.find(x => x.id == reconcileId),
                isChangeApproved: false
            }
            updateOrderItemChange(newChange)
        }
    }

    const onClickReturnSticker = (reconcileId: number): void => {

        let index: number = orderItemChanges.findIndex(reason => reason.reconcileReasonId === reconcileId)
        setSelectedOrderItemChanges({
            ...selectedOrderItemChanges,
            id: orderItemChanges[index].id!,
            reconcileReasonId: orderItemChanges[index].reconcileReasonId,
            refusalChoiceId: orderItemChanges[index].refusalChoiceId,
            returnSticker: orderItemChanges[index].returnSticker,
            isReturnStickerDuplicate: isReturnStickerDuplicate(orderItemChanges[index].returnSticker, null, orderItemChanges[index].id, null, null),
            noReturnSticker: orderItemChanges[index].noReturnStickerReason,
            comments: orderItemChanges[index].refusalComment,
            codeDate: orderItemChanges[index].codeDate,
            enableButton: true
        })
        setIsReturnStickerAvailable(orderItemChanges[index].noReturnStickerReason == null ? true : false)
        if (reconcileId != ReconcileReasonType.Destroyed && reconcileId != ReconcileReasonType.ReturnHold)
            setSecondaryPopupVisible(true)
    }

    const onConfirmReturnSticker = async (): Promise<void> => {
        let change: OrderItemChange | undefined = orderItemChanges.find(change => change.id === selectedOrderItemChanges.id)
        if (change) {
            change = {
                ...change,
                refusalChoiceId: selectedOrderItemChanges.refusalChoiceId,
                returnSticker: selectedOrderItemChanges.returnSticker,
                noReturnStickerReason: selectedOrderItemChanges.noReturnSticker,
                refusalComment: selectedOrderItemChanges.comments,
                codeDate: selectedOrderItemChanges.codeDate,
            }

            setSecondaryPopupVisible(false)
            updateOrderItemChange(change)
        }
    }

    const onValueChanged = (secondaryPopUpField: SecondaryPopUpField, value: any): void => {

        let newOrderItemChange: SecondaryPopUpValue = selectedOrderItemChanges
        switch (secondaryPopUpField) {
            case SecondaryPopUpField.RefusalChoiceId:
                newOrderItemChange = { ...newOrderItemChange!, refusalChoiceId: value }
                break;
            case SecondaryPopUpField.ReturnSticker:
                newOrderItemChange = { ...newOrderItemChange!, returnSticker: value == 0 ? null : +value, noReturnSticker: null }
                setIsReturnStickerAvailable(true)
                break;
            case SecondaryPopUpField.NoReturnSticker:
                newOrderItemChange = { ...newOrderItemChange!, noReturnSticker: value == "" ? null : value, returnSticker: null }
                setIsReturnStickerAvailable(false)
                break;
            case SecondaryPopUpField.Comments:
                newOrderItemChange = { ...newOrderItemChange!, comments: value }
                break;
            case SecondaryPopUpField.CodeDate:
                newOrderItemChange = { ...newOrderItemChange!, codeDate: value }
                break;
        }

        newOrderItemChange.isReturnStickerDuplicate = isReturnStickerDuplicate(newOrderItemChange.returnSticker, null, newOrderItemChange.id, null, null);

        setSelectedOrderItemChanges(newOrderItemChange)
    }


    return (
        <Fragment>
            <div className={'refusal-qty-adjust-header'}>
                <div className={'refusal-qty-adjust-text'}>Ordered: {orderItem.orderedQuantity}</div>
                <div className={'refusal-qty-adjust-text'}>Counted: {orderItem.countedQuantity}</div>
                <div className={'refusal-qty-adjust-text'}>Delivered: {deliveredQuantity}</div>
            </div>
            <Fragment>
                {reconcileReasons.map(reconcileReason =>
                    <AddRefusalCard
                        key={reconcileReason.id}
                        orderItemId={orderItem.id}
                        reconcileReasonId={reconcileReason.id}
                        onQuantityAdjusted={(quantity) => handleItemQuantityChanged(reconcileReason.id, quantity)}
                        onClickReturnSticker={() => onClickReturnSticker(reconcileReason.id)}
                    />
                )}
            </Fragment>
            {isSecondaryPopupVisible &&
                <SecondaryPopup
                    isVisible={isSecondaryPopupVisible}
                    isReturnStickerAvailable={isReturnStickerAvailable}
                    heading={'Additional Reason'}
                    secondaryPopUpValue={selectedOrderItemChanges}
                    onClickOk={onConfirmReturnSticker}
                    onClickCancel={() => setSecondaryPopupVisible(false)}
                    onValueChanged={onValueChanged}
                    refusalReasons={refusalReasons}
                >
                </SecondaryPopup>
            }
        </Fragment>
    )
};

